import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { MDSImage, MDSTypography } from '@marqvision/mds-v2';
import { useLogger } from '~/hooks';
import { UseViewModel } from '../@hooks/useViewModel';
import { LOG_EVENT_NAME } from '../../../@constants';

const useStyles = makeStyles(({ color }) => ({
  wrapper: {
    height: '99px',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    borderRadius: '8px',
    overflow: 'hidden',
    border: `1px solid ${color.border.neutral.weak.normal}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: color.bg.surface.neutral.default.hover,
    },
  },
  content: {
    padding: '16px',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gap: '6px',
  },
}));

type NeverProps<T> = {
  [K in keyof T]?: never;
};

type LoadingProps = {
  isLoading: true;
  data?: never;
  onClick?: never;
} & NeverProps<UseViewModel['listItemProps']>;

type LoadedProps = {
  isLoading?: false;
  data: UseViewModel['list'][0]['list'][0];
  onClick: (id: number) => void;
} & UseViewModel['listItemProps'];

type Props = LoadingProps | LoadedProps;

export const ListItem = (props: Props) => {
  const { isLoading, data, onClick, read } = props;
  const classes = useStyles();

  const { amplitudeLogging } = useLogger();

  const isRead = data ? read.check(data.id) : false;
  const color = isRead ? 'color/content/neutral/secondary/normal' : undefined;

  const handleClick = !isLoading
    ? () => {
        amplitudeLogging(LOG_EVENT_NAME.viewedReleaseNoteDetail, { 'Release note id': data.id });
        read.action(data.id);
        onClick(data.id);
      }
    : undefined;

  return (
    <div className={classes.wrapper} onClick={handleClick}>
      <div className={classes.content}>
        <MDSTypography variant="title" size="s" weight="semibold" lineClamp={1} color={color}>
          {isLoading ? <Skeleton /> : data.title}
        </MDSTypography>
        <MDSTypography lineClamp={2} color={color}>
          {isLoading ? (
            <>
              <Skeleton />
              <Skeleton />
            </>
          ) : (
            data.content
          )}
        </MDSTypography>
      </div>
      {data?.imageUrl && <MDSImage removeBorderRadius width="172px" height="100%" src={data.imageUrl} />}
    </div>
  );
};
